import { Badge, Button, Card, CardContent, Input, Select, Spinner } from '@crm/ui';
import { ChevronRight, Pencil, Plus, Power, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, ConfirmDialog, EmptyState, FilterBar, PageHeader, Pagination } from '@/components';
import { useDebounce, useDocumentTitle } from '@/hooks';
import { useCan, useSession } from '@/modules/auth/hooks';
import { roleLabel, toErrorMessage } from '@/modules/auth/utils';
import { USER_STATUS_OPTIONS, USERS_ROUTES, type UserStatusFilter } from '../constants';
import { useDeleteUser, useUpdateUser, useUsers } from '../hooks';

export function UsersPage() {
  useDocumentTitle('Usuarios');

  const canWrite = useCan('users', 'WRITE');
  const { user: currentUser } = useSession();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<UserStatusFilter>('all');
  const debouncedSearch = useDebounce(search, 300);

  const users = useUsers({ page, perPage, search: debouncedSearch, status });
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const actionError = updateUser.error ?? deleteUser.error;
  const meta = users.data?.meta;

  const [pendingRemoval, setPendingRemoval] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Users />}
        title="Usuarios"
        description="Cadastro, perfis de acesso e ativacao de contas."
        actions={
          canWrite ? (
            <Button asChild>
              <Link to={USERS_ROUTES.NEW}>
                <Plus className="h-4 w-4" />
                <span>
                  Novo<span className="hidden sm:inline"> usuario</span>
                </span>
              </Link>
            </Button>
          ) : null
        }
      />

      {/* No celular a busca fica com o botao de busca avancada ao lado; no
          desktop os dois filtros voltam a ter largura propria. */}

      {actionError ? (
        <p className="text-sm text-destructive">{toErrorMessage(actionError)}</p>
      ) : null}

      <Card variant="flush">
        <CardContent className="space-y-6">
          <FilterBar
            className="sm:flex sm:flex-row sm:flex-wrap sm:items-center"
            activeCount={status !== 'all' ? 1 : 0}
            search={
              <Input
                className="sm:max-w-xs"
                placeholder="Buscar por nome ou e-mail"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            }
          >
            <Select
              className="sm:max-w-40"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as UserStatusFilter);
                setPage(1);
              }}
            >
              {USER_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FilterBar>

          {users.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : users.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(users.error)}</p>
          ) : users.data && users.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {users.data.data.map((user) => {
                const isSelf = user.id === currentUser?.id;

                return (
                  <li
                    key={user.id}
                    // Acoes a direita, na mesma linha do nome: empilhadas, cada
                    // usuario gastava duas alturas no celular.
                    className="flex items-center gap-2 py-3 first:pt-0 last:pb-0"
                  >
                    {/* A linha toda abre a conta: no celular o alvo e o
                        registro inteiro, nao um icone no canto. */}
                    <Link
                      to={USERS_ROUTES.detail(user.id)}
                      className="flex min-w-0 flex-1 items-center gap-3"
                    >
                      <Avatar name={user.name} image={user.image} />

                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{user.name}</span>
                          <Badge variant={user.isActive ? 'success' : 'destructive'}>
                            {user.isActive ? 'Ativo' : 'Inativo'}
                          </Badge>
                          {user.profiles.map((profile) => (
                            <Badge key={profile.id} variant="outline">
                              {profile.name}
                            </Badge>
                          ))}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {user.email} - {roleLabel(user.role)}
                        </span>
                      </span>
                    </Link>

                    <ChevronRight
                      aria-hidden
                      className="h-5 w-5 shrink-0 text-muted-foreground sm:hidden"
                    />

                    {canWrite ? (
                      <div className="hidden shrink-0 items-center sm:flex">
                        <Button variant="ghost" size="icon" title="Editar" asChild>
                          <Link to={USERS_ROUTES.detail(user.id)}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          title={
                            isSelf
                              ? 'Nao e possivel alterar o proprio status'
                              : user.isActive
                                ? 'Inativar'
                                : 'Ativar'
                          }
                          disabled={isSelf || updateUser.isPending}
                          onClick={() =>
                            updateUser.mutate({
                              id: user.id,
                              input: { isActive: !user.isActive },
                            })
                          }
                        >
                          <Power className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          title={isSelf ? 'Nao e possivel excluir a propria conta' : 'Excluir'}
                          disabled={isSelf || deleteUser.isPending}
                          onClick={() => setPendingRemoval({ id: user.id, name: user.name })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <span className="hidden text-xs text-muted-foreground sm:inline">
                        Somente leitura
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState icon={<Users />} title="Nenhum usuario encontrado" />
          )}
        </CardContent>
      </Card>

      <Pagination
        meta={meta}
        label="usuarios"
        onChange={setPage}
        perPage={perPage}
        onPerPageChange={(value) => {
          setPerPage(value);
          setPage(1);
        }}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir usuario"
        message={`O usuario ${pendingRemoval?.name ?? ''} perde o acesso ao sistema. Esta acao nao pode ser desfeita.`}
        loading={deleteUser.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteUser.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
